import { useState, useEffect } from 'react';
import { getCategories } from '../../api/video-library';
import { getAllPlaylistMappings } from '../../api/contentMapping';

/**
 * The usePlaylistMapping custom hook fetches categories and playlist mappings data based on the
 * owner's email and allows for updating playlist information.
 * @param {string} ownerEmail - The email of the owner to fetch playlist mappings for.
 * @returns {Object} An object containing categories, allPlaylists, and updatePlaylist function.
 */
const usePlaylistMapping = (ownerEmail) => {
	const [allPlaylists, setAllPlaylists] = useState([]);
	const [categories, setCategories] = useState([]);

	/**
	 * Updates a specific field of a playlist at a given index.
	 *
	 * @param {number} index - The index of the playlist to update.
	 * @param {string} field - The field of the playlist to update.
	 * @param {any} value - The new value to set for the specified field.
	 */
	const updatePlaylist = (index, field, value) => {
		const clonedPlaylists = structuredClone(allPlaylists);
		clonedPlaylists[index] = { ...clonedPlaylists[index], [field]: value };
		setAllPlaylists(clonedPlaylists);
	};

	/**
	 * Fetches categories and playlist mappings from the server.
	 */
	const fetchData = async () => {
		try {
			const [categoryResponse, allPlaylistMappingsResponse] = await Promise.all([
				getCategories(),
				getAllPlaylistMappings(ownerEmail)
			]);

			const categoriesResponse = categoryResponse?.data?.data?.categories;
			delete categoriesResponse['dateCreated'];
			const filteredCategories = Object.keys(categoriesResponse);
			setCategories(filteredCategories);
			setAllPlaylists(allPlaylistMappingsResponse?.data?.data?.allPlaylistMappings || []);
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		if (ownerEmail) {
			fetchData();
		}
	}, [ownerEmail]);

	return {
		categories,
		allPlaylists,
		updatePlaylist
	};
};

export default usePlaylistMapping;
